// src/components/Home/Hero.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, animate } from 'framer-motion';

const Hero: React.FC = () => {
  const [currentBackground, setCurrentBackground] = useState(0);
  const [imageErrors, setImageErrors] = useState<boolean[]>([]);
  const count = useMotionValue(0);

  // Background images with fallbacks
  const backgroundImages = [
    {
      image: '/images/hero-bg-1.jpg',
      fallback: '/images/about-1.jpeg',
    },
    {
      image: '/images/hero-bg-6.jpg', 
      fallback: '/images/about-1.jpeg'
    },
    {
      image: '/images/random-2.jpg',
      fallback: '/images/about-1.jpeg',
    }
  ];

  const stats = [
    { value: 10, suffix: 'K+', label: 'Lives Impacted' },
    { value: 10, suffix: '+', label: 'Communities Reached' },
    { value: 10, suffix: 'K+', label: 'Trees Planted' },
    { value: 5, suffix: 'K+', label: 'Youth Empowered' },
  ];

  useEffect(() => {
    const backgroundInterval = setInterval(() => {
      setCurrentBackground((prev) => (prev + 1) % backgroundImages.length);
    }, 5000);

    return () => clearInterval(backgroundInterval);
  }, []);

  useEffect(() => {
    const animation = animate(count, 100, { duration: 2, ease: "easeOut" });
    return animation.stop;
  }, []);

  const handleImageError = (index: number) => {
    setImageErrors(prev => {
      const newErrors = [...prev];
      newErrors[index] = true;
      return newErrors;
    });
  };

  const getImageUrl = (index: number) => {
    return imageErrors[index] ? backgroundImages[index].fallback : backgroundImages[index].image;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const buttonVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "backOut"
      }
    },
    hover: {
      scale: 1.05,
      transition: {
        duration: 0.2,
        ease: "easeInOut"
      }
    },
    tap: {
      scale: 0.95
    }
  };

  const statVariants = {
    hidden: { opacity: 0, scale: 0 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "backOut"
      }
    }
  };

  return (
    <section 
      className="hero-section min-vh-100 d-flex align-items-center position-relative overflow-hidden"
      style={{
        marginTop: '76px',
        backgroundColor: '#f8f9fa' // Fallback background
      }}
    >
      {/* Background Images with Transition - USING IMG TAGS */}
      <div className="position-absolute top-0 start-0 w-100 h-100">
        {backgroundImages.map((_bg, index) => (
          <motion.div
            key={index}
            className="position-absolute top-0 start-0 w-100 h-100"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: index === currentBackground ? 1 : 0 
            }}
            transition={{ duration: 1.5, ease: "easeInOut" }}
            style={{
              zIndex: 1,
            }}
          >
            <img
              src={getImageUrl(index)}
              alt={`Background ${index + 1}`}
              className="w-100 h-100 object-fit-cover"
              onError={() => handleImageError(index)}
              style={{
                filter: 'brightness(0.9)',
              }}
            />
            {/* Overlay for better text readability */}
            <div 
              className="position-absolute top-0 start-0 w-100 h-100"
              style={{
                background: 'linear-gradient(135deg, rgba(250, 250, 250, 0.4) 0%, rgba(245, 245, 245, 0.6) 100%)',
              }}
            />
          </motion.div>
        ))}
      </div>

      {/* Subtle Background Elements */}
      <div className="position-absolute top-0 start-0 w-100 h-100" style={{ zIndex: 2 }}>
        <motion.div
          className="position-absolute"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
          style={{
            top: '10%',
            left: '5%',
            width: '100px',
            height: '100px',
            background: 'radial-gradient(circle, rgba(180, 180, 180, 0.1) 0%, transparent 70%)',
            borderRadius: '50%'
          }}
        />
        <motion.div
          className="position-absolute"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.7 }}
          style={{
            bottom: '20%',
            right: '10%',
            width: '150px',
            height: '150px',
            background: 'radial-gradient(circle, rgba(180, 180, 180, 0.1) 0%, transparent 70%)',
            borderRadius: '50%'
          }}
        />
      </div>

      {/* Main Content */}
      <div className="container position-relative z-3">
        <motion.div
          className="row justify-content-center text-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="col-lg-10 col-xl-8">
            <motion.div variants={itemVariants}>
              <motion.h1 
                className="display-3 fw-black mb-4 gradient-text"
                style={{
                  background: 'linear-gradient(135deg, #4a5568 0%, #2d3748 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Building Sustainable
                <motion.span 
                  className="d-block highlight-text"
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                >
                  Futures in Africa
                </motion.span>
              </motion.h1>
            </motion.div>

            <motion.div variants={itemVariants}>
              <motion.p 
                className="lead mb-5 fs-3 fw-medium text-gray-800"
                style={{ lineHeight: '1.6' }}
              >
                Empowering communities through sustainable food security, 
                <span className="d-block">climate resilience, and youth empowerment initiatives</span>
                across Africa.
              </motion.p>
            </motion.div>

            <motion.div 
              className="d-flex flex-column flex-sm-row justify-content-center gap-4 mb-5"
              variants={itemVariants}
            >
              <motion.div
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <Link 
                  to="/donate" 
                  className="btn btn-lg px-5 py-3 fw-bold shadow-lg donate-btn"
                  style={{
                    background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                    border: 'none',
                    borderRadius: '50px',
                    fontSize: '1.1rem',
                    color: 'white'
                  }}
                >
                  💚 Donate Now
                </Link>
              </motion.div>
              
              <motion.div
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <Link 
                  to="/volunteer" 
                  className="btn btn-outline-secondary btn-lg px-5 py-3 fw-bold shadow-lg volunteer-btn"
                  style={{
                    border: '2px solid #718096',
                    borderRadius: '50px',
                    fontSize: '1.1rem',
                    color: '#4a5568',
                    backgroundColor: 'rgba(255,255,255,0.9)'
                  }}
                >
                  🤝 Become a Volunteer
                </Link>
              </motion.div>
            </motion.div>

            {/* Stats Section */}
            <motion.div 
              className="row justify-content-center mt-5 pt-4"
              variants={containerVariants}
            >
              <div className="col-12">
                <div className="row g-4">
                  {stats.map((stat, index) => (
                    <motion.div 
                      key={stat.label}
                      className="col-6 col-md-3"
                      variants={statVariants}
                      custom={index}
                    >
                      <div className="text-center">
                        <motion.div 
                          className="stat-value fw-black mb-1"
                          style={{
                            background: 'linear-gradient(135deg, #4a5568 0%, #2d3748 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontSize: '2rem'
                          }}
                          whileHover={{ scale: 1.1 }}
                        >
                          {stat.value}{stat.suffix}
                        </motion.div>
                        <div 
                          className="stat-label fw-medium text-gray-700"
                          style={{ fontSize: '0.9rem' }}
                        >
                          {stat.label}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div 
        className="position-absolute bottom-0 start-50 translate-middle-x mb-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 2 }}
        style={{ zIndex: 3 }}
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          style={{
            width: '30px',
            height: '50px',
            border: '2px solid rgba(74, 85, 104, 0.5)',
            borderRadius: '25px',
            display: 'flex',
            justifyContent: 'center',
            paddingTop: '10px',
            backgroundColor: 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <motion.div
            animate={{ y: [0, 15, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            style={{
              width: '4px',
              height: '10px',
              backgroundColor: 'rgba(74, 85, 104, 0.8)',
              borderRadius: '2px'
            }}
          />
        </motion.div>
      </motion.div>

      {/* Custom Styles */}
      <style>{`
        .hero-section {
          position: relative;
        }
        
        .object-fit-cover {
          object-fit: cover;
        }
        
        .gradient-text {
          font-size: clamp(2.5rem, 5vw, 4rem);
          line-height: 1.1;
        }
        
        .highlight-text {
          background: linear-gradient(135deg, #4a5568 0%, #2d3748 100%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          position: relative;
        }
        
        .highlight-text::after {
          content: '';
          position: absolute;
          bottom: -5px;
          left: 0;
          width: 100%;
          height: 3px;
          background: linear-gradient(135deg, #4a5568 0%, #2d3748 100%);
          border-radius: 2px;
          opacity: 0.7;
        }
        
        .donate-btn:hover {
          background: linear-gradient(135deg, #38a169 0%, #2f855a 100%) !important;
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(72, 187, 120, 0.3) !important;
        }
        
        .volunteer-btn:hover {
          background: rgba(113, 128, 150, 0.15) !important;
          border-color: #4a5568 !important;
          color: #2d3748 !important;
          transform: translateY(-2px);
          backdrop-filter: blur(10px);
        }
        
        .stat-value {
          font-size: clamp(1.5rem, 3vw, 2rem);
        }
        
        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
        
        /* Mobile optimizations */
        @media (max-width: 768px) {
          .hero-section {
            min-height: 100vh;
            padding-top: 2rem;
            padding-bottom: 2rem;
          }
          
          .display-3 {
            font-size: 2.5rem !important;
          }
          
          .lead {
            font-size: 1.25rem !important;
          }
        }
      `}</style>
    </section>
  );
};

export default Hero;